// What a score lookup hands back, and the shape the card draws from.
//
// rgip's `presentation.py` puts this on the `activity_end` of `springbok_score_lookup`,
// nested under `receipt.card`, exactly as a tree write does for a split. The reason is
// the same and the evidence is the same: a score is a CHAIN (the trees blend, an
// override may cap the result, signals land on the record) and the agent was restating
// every link of it in prose, which made the reader hold four paragraphs in their head to
// answer one question.
//
// Nothing on the card is phrased by rgip. The condition arrives as stored SQL for
// `conditionTraits`, the renderer a tree node's own condition already goes through; the
// override arrives as a whole `Rule` for `overrideRuleSentence`; and the signal labels
// are the sentences Studio itself composed for the lead sample. All three wordings are
// owned here, so the card and the page it sits beside cannot describe one thing two ways.
import { Rule } from '../../../models/rules';
import { Threshold } from '../../../models/ModelPerformances';

/**
 * One tree that carries weight: what it said, and how much of the blend it is.
 *
 * No walk. The first-glance question is whether the trees agreed and which one carried
 * the weight; the path that got each one there is a click away on the leaf this row
 * links to, and the score lookup page draws it in full. Repeating it in a chat bubble
 * was a second copy of that page.
 */
export interface ScoreCardTree {
  tree?: number;
  name?: string;
  weight?: number;
  /** The band this tree's own leaf rate falls in, already resolved. */
  segment?: string | null;
  heaviest?: boolean;
  leaf?: number | null;
  link?: string;
}

/** The rule that decided the segment, when one did. */
export interface ScoreCardOverride {
  moved_segment?: boolean;
  score_before?: number | null;
  segment_before?: string | null;
  rule_index?: number | null;
  rule?: Rule | null;
}

/** One flag this record would carry into the CRM. */
export interface ScoreCardSignal {
  group?: string;
  /** The sentence Sales reads, as Studio composed it. */
  label?: string;
  argues_for?: boolean;
}

/** One rule group that fired on a rule based record: what it added, and why. */
export interface ScoreCardRule {
  points: number;
  /** The values the fired rule read, as `field = value`. */
  because?: string | null;
}

export interface ScoreCardData {
  kind: 'score';
  /** `points` on a rule based model, whose card lists `rules` in place of `trees`. */
  base?: string;
  rules?: ScoreCardRule[];
  /** What the fired rules add up to: the score before any override. */
  rules_total?: number | null;
  email?: string;
  score?: number | null;
  segment?: string | null;
  trees: ScoreCardTree[];
  /** Trees at weight 0. Counted, never drawn: they contributed nothing and their walks
   *  read exactly like the ones that did. */
  unused_trees?: number;
  signals?: ScoreCardSignal[];
  /** The reasons on the record contradict the score it carries. */
  signals_disagree?: boolean;
  override?: ScoreCardOverride;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * The score card a receipt carries, or null when it carries none.
 *
 * Null is ordinary, not a failure: every other tool's receipt has no score card, and a
 * lookup that came back without a segment sends none rather than a header with no
 * outcome under it. Validated rather than cast, for the same reason `splitCardFrom` is.
 */
export function scoreCardFrom(receipt: unknown): ScoreCardData | null {
  if (!isObject(receipt)) return null;
  const card = receipt.card;
  if (!isObject(card) || card.kind !== 'score') return null;
  if (!Array.isArray(card.trees) || !card.segment) return null;
  return card as unknown as ScoreCardData;
}

/**
 * The segment as one of the four the rest of Studio knows, or null.
 *
 * Narrowed rather than trusted: the scoring writes `very good` / `good` / `medium` /
 * `low` and the tree page's colour, label and contrast helpers are all keyed on exactly
 * those. Anything else gets no fill instead of an undefined lookup painting the chip
 * transparent on transparent.
 */
export function asThreshold(segment: string | null | undefined): Threshold | null {
  const seat = (segment ?? '').trim().toLowerCase();
  return (['very good', 'good', 'medium', 'low'] as Threshold[]).find(
    (known) => known === seat
  ) ?? null;
}

/**
 * `60%` of the blend, or null when the tree carries no stated weight.
 *
 * A weight is ALWAYS a fraction. `WeightAllocationBar` is what writes one: it
 * renders `weights[tree] * 100` and stores `next / 100`, so nothing in the product
 * puts a percentage in that field. This used to guard `weight <= 1 ? weight * 100 :
 * weight` under a comment claiming both forms existed, which was inferred from the
 * shape of a value rather than read off the writer. The guard was dead code that
 * would one day have rendered a legitimate 0.6 as `0.6%`, and it disagreed with
 * `ScoreLookupPage`, which multiplies flat. One reading, in one place.
 */
export function weightText(weight: number | undefined): string | null {
  if (typeof weight !== 'number' || !Number.isFinite(weight)) return null;
  return `${Math.round(weight * 100)}%`;
}

/**
 * `tree_1` -> `Tree 1`, the way every other surface writes it.
 *
 * The stored name is a slug and Studio never shows it raw: the tree page, the weights
 * table and the score lookup page all say "Tree 1". A card saying `tree_1` beside them
 * reads as a different thing with a similar name. Anything that is not the slug shape is
 * a name somebody chose, and is left exactly as they wrote it.
 */
export function treeLabel(name: string | undefined, id: number | undefined): string {
  const slug = /^tree[_ -]?(\d+)$/i.exec((name ?? '').trim());
  if (slug) return `Tree ${slug[1]}`;
  const named = (name ?? '').trim();
  return named || (id === undefined ? 'Tree' : `Tree ${id}`);
}

/** `73,171`, or the value as written when it is not a number. */
export function valueText(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return typeof value === 'number' ? value.toLocaleString() : String(value);
}
